/**
 * Prise en charge d'une urgence, depuis l'accueil comme depuis sa fiche, même
 * sans réseau (WEB1-20) : une seule file, celle de l'application
 * (`offline/sync.ts`), qui tourne sur tous les écrans. Sans réseau, le geste y
 * part avec son heure ; au retour du réseau, `/sync/push` l'applique avec les
 * mêmes droits que la route en ligne. L'écran distingue toujours en file,
 * confirmée et échouée (déjà prise par quelqu'un d'autre, alerte close) ; rien
 * n'est perdu en silence, et le geste se voit dans « Envois en attente ».
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { api, ApiError } from '../../core/api.js';
import { t } from '../../core/i18n.js';
import { discardRefused, enqueueOperation, getQueuedOperations, onSyncState } from '../../offline/sync.js';
import type { OutboxOperation } from '../../offline/store.js';

export type AckStatus = 'pending' | 'queued' | 'confirmed' | 'failed';

export interface AckState {
  status: AckStatus;
  message?: string;
}

const KIND = 'emergency_alert';
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Ce que devient un geste après une réponse en erreur : échoué (le réseau manquant, lui, met en file). */
export function ackOutcome(error: unknown): AckState {
  if (error instanceof ApiError && error.code === 'transition_interdite') {
    return { status: 'failed', message: t('urgences.prise.dejaPrise') };
  }
  return { status: 'failed', message: error instanceof Error ? error.message : t('urgences.prise.echec') };
}

const isAck = (op: OutboxOperation): boolean => op.entityKind === KIND && op.opKind === 'action' && op.patch.action === 'ack';

/**
 * Prendre en charge : en ligne tout de suite, sinon mis en file avec l'heure du
 * geste (une seule fois par alerte). Rend `queued` ou `confirmed` ; un refus
 * du serveur remonte en erreur.
 */
export async function acknowledgeEmergency(alertId: string): Promise<'queued' | 'confirmed'> {
  const occurredAt = new Date().toISOString();
  try {
    await api(`/emergency-alerts/${alertId}/ack`, { method: 'POST' });
    return 'confirmed';
  } catch (error) {
    if (!(error instanceof ApiError && error.code === 'network_error')) throw error;
    const already = (await getQueuedOperations(KIND, ['attente', 'envoi', 'erreur']))
      .some((op) => isAck(op) && op.entityId === alertId);
    if (!already) {
      await enqueueOperation({ entityKind: KIND, entityId: alertId, opKind: 'action', patch: { action: 'ack', occurredAt } });
    }
    return 'queued';
  }
}

/** Âge au-delà duquel l'ancienne file abandonnait une prise en charge (et le serveur la refuse). */
const LEGACY_MAX_AGE_MS = 24 * 3_600_000;

/** File de l'accueil d'avant le 20/10/2026 (localStorage, propre au compte). */
const legacyKey = (userId: string): string => `myequip.accueil.prises-en-charge.${userId}`;

/**
 * Reprise unique de l'ancienne file de l'accueil : chaque prise en charge en
 * attente rejoint la file de l'application avec l'heure de son geste, puis
 * l'ancienne copie est effacée. Une entrée illisible, ou de plus de 24 h
 * (que l'ancienne file abandonnait déjà et que le serveur refuse), est ignorée.
 */
export async function adoptLegacyAcks(userId: string): Promise<number> {
  const now = Date.now();
  let raw: unknown;
  try {
    raw = JSON.parse(localStorage.getItem(legacyKey(userId)) ?? '[]');
  } catch {
    raw = [];
  }
  const entries = (Array.isArray(raw) ? raw : []).filter((entry): entry is { alertId: string; at: number } =>
    Boolean(entry) && typeof entry === 'object'
    && typeof (entry as { alertId?: unknown }).alertId === 'string' && UUID.test((entry as { alertId: string }).alertId)
    && typeof (entry as { at?: unknown }).at === 'number' && Number.isFinite((entry as { at: number }).at))
    // Comme l'ancienne file : au-delà de 24 h (ou datée du futur), elle était déjà abandonnée sans bruit.
    .filter((entry) => entry.at <= now + 60_000 && now - entry.at <= LEGACY_MAX_AGE_MS);
  const known = new Set((await getQueuedOperations(KIND, ['attente', 'envoi', 'erreur'])).filter(isAck).map((op) => op.entityId));
  let adopted = 0;
  for (const entry of entries) {
    if (known.has(entry.alertId)) continue;
    known.add(entry.alertId);
    await enqueueOperation({
      entityKind: KIND, entityId: entry.alertId, opKind: 'action',
      patch: { action: 'ack', occurredAt: new Date(entry.at).toISOString() },
    });
    adopted += 1;
  }
  try {
    localStorage.removeItem(legacyKey(userId));
  } catch {
    // Stockage inaccessible : l'entrée sera reprise au prochain essai, sans doublon.
  }
  return adopted;
}

const queuedState = (): AckState => ({ status: 'queued', message: t('urgences.prise.enFile') });
const confirmedState = (): AckState => ({ status: 'confirmed', message: t('urgences.prise.confirmee') });

/**
 * États des prises en charge d'un écran et le geste qui les déclenche. Une
 * prise en charge en file est relue à chaque mouvement de la file : confirmée
 * quand le serveur l'applique, échouée (avec son motif) quand il la refuse.
 */
export function useEmergencyAcks(userId: string | undefined, onConfirmed: () => void): {
  states: Record<string, AckState>;
  acknowledge: (alertId: string) => Promise<void>;
  dismiss: (alertId: string) => void;
} {
  const [states, setStates] = useState<Record<string, AckState>>({});
  const tracked = useRef(new Map<string, string>());
  const confirmedRef = useRef(onConfirmed);
  confirmedRef.current = onConfirmed;
  const set = useCallback((alertId: string, state: AckState) => {
    setStates((current) => ({ ...current, [alertId]: state }));
  }, []);

  useEffect(() => {
    if (!userId) return undefined;
    let alive = true;
    const read = async (): Promise<void> => {
      const operations = (await getQueuedOperations(KIND, ['attente', 'envoi', 'erreur', 'refusee', 'succes'])).filter(isAck);
      if (!alive) return;
      let anyConfirmed = false;
      for (const op of operations) {
        if (op.status === 'attente' || op.status === 'envoi' || op.status === 'erreur') {
          tracked.current.set(op.entityId, op.opId);
          set(op.entityId, queuedState());
        } else if (op.status === 'refusee') {
          tracked.current.set(op.entityId, op.opId);
          set(op.entityId, { status: 'failed', message: op.lastError ?? t('urgences.prise.echec') });
        } else if (tracked.current.get(op.entityId) === op.opId) {
          // Envoyée depuis l'ouverture de l'écran : confirmée une fois, pas à chaque relecture.
          tracked.current.delete(op.entityId);
          set(op.entityId, confirmedState());
          anyConfirmed = true;
        }
      }
      if (anyConfirmed) confirmedRef.current();
    };
    void adoptLegacyAcks(userId).catch(() => 0).then(() => {
      if (alive) void read().catch(() => undefined);
    });
    const stop = onSyncState(() => { void read().catch(() => undefined); });
    return () => {
      alive = false;
      stop();
    };
  }, [set, userId]);

  const acknowledge = useCallback(async (alertId: string) => {
    if (!userId) return;
    set(alertId, { status: 'pending' });
    try {
      const outcome = await acknowledgeEmergency(alertId);
      if (outcome === 'confirmed') {
        set(alertId, confirmedState());
        confirmedRef.current();
      } else {
        set(alertId, queuedState());
      }
    } catch (error) {
      set(alertId, ackOutcome(error));
    }
  }, [set, userId]);

  const dismiss = useCallback((alertId: string) => {
    // Un refus fermé est soldé : il ne revient pas à la prochaine ouverture.
    const opId = tracked.current.get(alertId);
    if (opId && states[alertId]?.status === 'failed') {
      tracked.current.delete(alertId);
      void discardRefused({ id: opId, kind: 'operation' });
    }
    setStates((current) => {
      const { [alertId]: _removed, ...rest } = current;
      return rest;
    });
  }, [states]);

  return { states, acknowledge, dismiss };
}
